import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import { DataTable, DataTableColumn } from '../../components/ui/data-table';
import StatsCard from '../../components/common/StatsCard';
import PerformanceChart from '../../components/trading/PerformanceChart';
import TradingStatusBadge from '../../components/trading/TradingStatusBadge';
import PageHeader from '../../components/common/PageHeader';
import {
  DollarSign,
  TrendingUp,
  Copy,
  Activity,
  Layers,
  Calendar,
} from 'lucide-react';

type Subscription = {
  id: string;
  traderName: string;
  strategyName: string;
  investment: number;
  equity: number;
  totalProfit: number;
  status: 'active' | 'paused' | 'stopped';
};

type LiveTrade = {
  id: string;
  symbol: string;
  type: 'BUY' | 'SELL';
  lots: number;
  entryPrice: number;
  currentPrice: number;
  profit: number;
  strategyName: string;
};

const MOCK_SUBSCRIPTIONS: Subscription[] = [
  {
    id: 'SUB001',
    traderName: 'John Smith',
    strategyName: 'Gold Scalper Pro',
    investment: 5000,
    equity: 5750,
    totalProfit: 750,
    status: 'active',
  },
  {
    id: 'SUB002',
    traderName: 'Jane Doe',
    strategyName: 'Forex Trend Master',
    investment: 3000,
    equity: 3450,
    totalProfit: 450,
    status: 'active',
  },
];

const MOCK_LIVE_TRADES: LiveTrade[] = [
  {
    id: 'LT001',
    symbol: 'XAUUSD',
    type: 'BUY',
    lots: 0.2,
    entryPrice: 2032.5,
    currentPrice: 2038.1,
    profit: 112.0,
    strategyName: 'Gold Scalper Pro',
  },
  {
    id: 'LT002',
    symbol: 'EURUSD',
    type: 'SELL',
    lots: 0.5,
    entryPrice: 1.0785,
    currentPrice: 1.0762,
    profit: 115.0,
    strategyName: 'Forex Trend Master',
  },
];

const CopyTradingDashboard = () => {
  const [subscriptions] = useState<Subscription[]>(MOCK_SUBSCRIPTIONS);
  const [liveTrades] = useState<LiveTrade[]>(MOCK_LIVE_TRADES);

  const totalInvestment = subscriptions.reduce(
    (acc, curr) => acc + curr.investment,
    0
  );
  const currentEquity = subscriptions.reduce(
    (acc, curr) => acc + curr.equity,
    0
  );
  const totalProfit = subscriptions.reduce(
    (acc, curr) => acc + curr.totalProfit,
    0
  );

  const subColumns: DataTableColumn<Subscription>[] = [
    {
      id: 'strategyName',
      header: 'Strategy',
      accessor: r => <span className="font-semibold">{r.strategyName}</span>,
      sortable: true,
    },
    {
      id: 'traderName',
      header: 'Master Trader',
      accessor: r => r.traderName,
      sortable: true,
    },
    {
      id: 'investment',
      header: 'Investment',
      accessor: r => `$${r.investment.toLocaleString()}`,
      sortable: true,
    },
    {
      id: 'equity',
      header: 'Equity',
      accessor: r => `$${r.equity.toLocaleString()}`,
      sortable: true,
    },
    {
      id: 'totalProfit',
      header: 'Profit',
      accessor: r => (
        <span
          className={
            r.totalProfit >= 0
              ? 'text-green-600 font-semibold'
              : 'text-red-600 font-semibold'
          }
        >
          ${r.totalProfit.toLocaleString()}
        </span>
      ),
      sortable: true,
    },
    {
      id: 'status',
      header: 'Status',
      accessor: r => <TradingStatusBadge status={r.status} />,
      sortable: true,
    },
  ];

  const tradeColumns: DataTableColumn<LiveTrade>[] = [
    {
      id: 'symbol',
      header: 'Symbol',
      accessor: r => <span className="font-semibold">{r.symbol}</span>,
      sortable: true,
    },
    {
      id: 'strategyName',
      header: 'Strategy',
      accessor: r => r.strategyName,
      sortable: true,
    },
    {
      id: 'type',
      header: 'Type',
      accessor: r => (
        <span
          className={`px-2 py-0.5 text-xs font-semibold rounded-full ${r.type === 'BUY' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
        >
          {r.type}
        </span>
      ),
      sortable: true,
    },
    { id: 'lots', header: 'Lot', accessor: r => r.lots, sortable: true },
    {
      id: 'entryPrice',
      header: 'Entry',
      accessor: r => r.entryPrice,
      sortable: true,
    },
    {
      id: 'currentPrice',
      header: 'Current Price',
      accessor: r => r.currentPrice,
      sortable: true,
    },
    {
      id: 'profit',
      header: 'Profit ($)',
      accessor: r => (
        <span
          className={
            r.profit >= 0
              ? 'text-green-600 font-semibold'
              : 'text-red-600 font-semibold'
          }
        >
          ${r.profit.toFixed(2)}
        </span>
      ),
      sortable: true,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="My Copy Trading Dashboard"
          description="Overview of your active strategy subscriptions, real-time copied trades, and equity performance"
        />
      </div>

      {/* Summary Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatsCard
          title="Active Strategies"
          value={subscriptions.length.toString()}
          change="Subscribed"
          icon={Copy}
        />
        <StatsCard
          title="Total Investment"
          value={`$${totalInvestment.toLocaleString()}`}
          change="Initial Capital"
          icon={DollarSign}
        />
        <StatsCard
          title="Current Equity"
          value={`$${currentEquity.toLocaleString()}`}
          change="Total Value"
          icon={Layers}
        />
        <StatsCard
          title="Total Profit"
          value={`+$${totalProfit.toLocaleString()}`}
          change="Lifetime Return"
          icon={TrendingUp}
        />
        <StatsCard
          title="Today's Profit"
          value="+$227.00"
          change="Last 24 Hours"
          icon={Activity}
        />
        <StatsCard
          title="Monthly Profit"
          value="+$1,200.00"
          change="This Month"
          icon={Calendar}
        />
      </div>

      {/* Performance Cards & Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Equity Growth Curve</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <PerformanceChart
              data={[
                { date: 'Jan', value: 5000 },
                { date: 'Feb', value: 5400 },
                { date: 'Mar', value: 5800 },
                { date: 'Apr', value: 6200 },
                { date: 'May', value: 6900 },
                { date: 'Jun', value: 7600 },
                { date: 'Jul', value: 8200 },
                { date: 'Aug', value: 9200 },
              ]}
              valueLabel="Equity ($)"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Monthly Profit Performance</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <PerformanceChart
              data={[
                { date: 'Jan', value: 400 },
                { date: 'Feb', value: 400 },
                { date: 'Mar', value: 400 },
                { date: 'Apr', value: 700 },
                { date: 'May', value: 700 },
                { date: 'Jun', value: 600 },
                { date: 'Jul', value: 1000 },
                { date: 'Aug', value: 1200 },
              ]}
              valueLabel="Monthly Profit ($)"
            />
          </CardContent>
        </Card>
      </div>

      {/* Tables: Active Subscriptions & Live Trades */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Active Subscriptions</CardTitle>
            <CardDescription>Strategies currently being copied</CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={subColumns}
              data={subscriptions}
              showAddButton={false}
              showExportButton={false}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Live Copied Open Positions</CardTitle>
            <CardDescription>
              Real-time trades currently active on your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={tradeColumns}
              data={liveTrades}
              showAddButton={false}
              showExportButton={false}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CopyTradingDashboard;
